/**
 * Centralized timezone utilities for frontend
 * All date operations should use UTC to ensure consistency across the platform
 */
class TimezoneUtils {
  /**
   * Get current date/time in UTC
   * @returns {Date} Current UTC date
   */
  static now() {
    return new Date();
  }

  /**
   * Convert a local date to UTC
   * @param {Date} date - Local date to convert
   * @returns {Date} UTC date
   */
  static toUTC(date) {
    return new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
  }

  /**
   * Get start of day in UTC (00:00:00.000)
   * @param {Date} date - Date to get start of day for (defaults to now)
   * @returns {Date} Start of day in UTC
   */
  static startOfDayUTC(date = new Date()) {
    const utcDate = new Date(date);
    return new Date(Date.UTC(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      0, 0, 0, 0
    ));
  }

  /**
   * Get end of day in UTC (23:59:59.999)
   * @param {Date} date - Date to get end of day for (defaults to now)
   * @returns {Date} End of day in UTC
   */
  static endOfDayUTC(date = new Date()) {
    const utcDate = new Date(date);
    return new Date(Date.UTC(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      23, 59, 59, 999
    ));
  }

  /**
   * Format date for API requests (ISO string)
   * @param {Date} date - Date to format
   * @returns {string} ISO string in UTC
   */
  static formatForAPI(date) {
    return date.toISOString();
  }

  /**
   * Parse date from API response (ISO string)
   * @param {string} dateString - ISO date string
   * @returns {Date} Parsed UTC date
   */
  static parseFromAPI(dateString) {
    return new Date(dateString);
  }

  /**
   * Format date for display in UTC
   * @param {Date} date - Date to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted date string
   */
  static formatForDisplay(date, options = {}) {
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      ...options
    });
  }

  /**
   * Format date for display in UTC (date only)
   * @param {Date} date - Date to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted date string
   */
  static formatDateForDisplay(date, options = {}) {
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      ...options
    });
  }

  /**
   * Format time for display in UTC (time only)
   * @param {Date} date - Date to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted time string
   */
  static formatTimeForDisplay(date, options = {}) {
    return date.toLocaleTimeString('en-US', {
      timeZone: 'UTC',
      ...options
    });
  }

  /**
   * Create UTC date from year, month, day, hour, minute, second
   * @param {number} year - Year
   * @param {number} month - Month (0-11)
   * @param {number} day - Day (1-31)
   * @param {number} hour - Hour (0-23)
   * @param {number} minute - Minute (0-59)
   * @param {number} second - Second (0-59)
   * @param {number} millisecond - Millisecond (0-999)
   * @returns {Date} UTC date
   */
  static createUTC(year, month, day, hour = 0, minute = 0, second = 0, millisecond = 0) {
    return new Date(Date.UTC(year, month, day, hour, minute, second, millisecond));
  }

  /**
   * Add days to a UTC date
   * @param {Date} date - Base date
   * @param {number} days - Number of days to add
   * @returns {Date} New UTC date
   */
  static addDaysUTC(date, days) {
    const newDate = new Date(date);
    newDate.setUTCDate(newDate.getUTCDate() + days);
    return newDate;
  }

  /**
   * Add hours to a UTC date
   * @param {Date} date - Base date
   * @param {number} hours - Number of hours to add
   * @returns {Date} New UTC date
   */
  static addHoursUTC(date, hours) {
    const newDate = new Date(date);
    newDate.setUTCHours(newDate.getUTCHours() + hours);
    return newDate;
  }

  /**
   * Get date range for a specific day in UTC
   * @param {Date|string} date - Date or date string
   * @returns {Object} Object with start and end of day in UTC
   */
  static getDayRangeUTC(date) {
    const targetDate = typeof date === 'string' ? new Date(date) : new Date(date);
    return {
      start: this.startOfDayUTC(targetDate),
      end: this.endOfDayUTC(targetDate)
    };
  }

  /**
   * Get date range for a specific number of days ago in UTC
   * @param {number} daysAgo - Number of days ago
   * @returns {Object} Object with start and end of range in UTC
   */
  static getDaysAgoRangeUTC(daysAgo) {
    const endDate = this.now();
    const startDate = this.addDaysUTC(endDate, -daysAgo);
    return {
      start: this.startOfDayUTC(startDate),
      end: this.endOfDayUTC(endDate)
    };
  }

  /**
   * Check if a date is within a UTC date range
   * @param {Date} date - Date to check
   * @param {Date} startDate - Start of range
   * @param {Date} endDate - End of range
   * @returns {boolean} True if date is within range
   */
  static isWithinRangeUTC(date, startDate, endDate) {
    const checkDate = new Date(date);
    return checkDate >= startDate && checkDate <= endDate;
  }

  /**
   * Get today's date in YYYY-MM-DD format for date inputs
   * @returns {string} Today's date in YYYY-MM-DD format
   */
  static getTodayForInput() {
    const today = this.now();
    return today.toISOString().split('T')[0];
  }

  /**
   * Format time from HH:mm string to UTC display
   * @param {string} timeString - Time in HH:mm format
   * @returns {string} Formatted time in UTC
   */
  static formatTimeFromString(timeString) {
    const [hours, minutes] = timeString.split(':');
    const date = this.createUTC(2025, 0, 1, parseInt(hours), parseInt(minutes));
    return this.formatTimeForDisplay(date, {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Generate an array of date strings in YYYY-MM-DD format for a range
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {string[]} Array of date strings
   */
  static generateDateRange(startDate, endDate) {
    const dates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      // Format as YYYY-MM-DD
      const year = currentDate.getUTCFullYear();
      const month = String(currentDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getUTCDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      
      // Move to next day
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
    
    return dates;
  }

  /**
   * Get day of week abbreviation (Mon, Tue, etc) for a date string
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @returns {string} Day of week abbreviation
   */
  static getDayOfWeek(dateString) {
    const date = new Date(dateString + 'T00:00:00Z'); // Parse as UTC
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getUTCDay()];
  }

  /**
   * Format date with day of week for chart labels
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @returns {string[]} Array with date and day of week
   */
  static formatDateWithDayOfWeek(dateString) {
    const dayOfWeek = this.getDayOfWeek(dateString);
    return [dateString, dayOfWeek];
  }
}

export default TimezoneUtils;
